/**
 * # Player
 *
 * This is the Animoto wrapper for JW Player.  It exposes the 'Player' Class
 *
 * @requires jwPlayer
 */
/*
  The JWPlayer SWF and Animoto css files for styling the player are required here
  so that they can be packaged and deployed properly by webpack
*/
import debounce from 'lodash.debounce';
import '../vendor/jwplayer.flash.swf';
import './player.css';
/*
  The JWPlayer javascript is imported and assigned to the jwplayer variable
*/
import '../vendor/jwplayer';
import errorCodes from './errorCodes';
import { uuidV4 } from './helpers';

export default class Player {
  /**
   * Constructor for the Player class.  Takes in the element and options
   * and instantiates a JWPlayer object on that element
   *
   * @method constructor
   *
   * @param  {String}  playerElementId   The ID of the element on the page in which to instantiate the player
   * @param  {Object}  videoOptions      All of the options necessary to instantiate the player (playlist, cover image, etc.)
   *
   * @example
   *  var somePlayer = new Player('player-element-id', {
   *    playlist: [{
   *      image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *      sources: [{
   *        file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *        label     : '1080p',
   *        'default' : true
   *      }]
   *    }],
   *    ctaSettings : {
   *      title : 'This is my CTA title',
   *      url   : 'http://www.animoto.com'
   *    },
   *    eventHandlers : {
   *      INITIALIZED : function(playerType) { console.log(playerType + ' was initialized'); },
   *      STARTED     : function(playerType) { console.log(playerType + ' started playing'); },
   *      COMPLETED   : function(playerType) { console.log(playerType + ' finished playing'); },
   *    },
   *    qosTracker : 'fake-domain.com/qos_trail.gif',
   *    qosStyle   : 'animoto_original',
   *    repeat     : false
   *    autostart  : false,
   *    mute       : false
   *  });
   *
   */
  constructor(playerElementId, videoOptions) {
    const defaults = {
      allowPlaylistSorting : true,
      aspectratio          : '16:9',
      width                : '100%',
      key                  : 'x1HV1DwUcPxvBsl28p8l85EWfnZuK1P7In6bCZTj4osw1giS',
      flashplayer          : '//d150hyw1dtprld.cloudfront.net/player/77f9abc9c2798b7a9c7678214dd3cf55db3285b6/jwplayer.flash.swf',
      preload              : 'none',
      skin                 : {
        active     : '#1bcdcf',
        inactive   : '#eeeeee',
        controlbar : {
          text        : '#eeeeee',
          icons       : '#eeeeee',
          iconsActive : '#1bcdcf'
        },
        tooltips : {
          text : '#505050'
        },
        menus : {
          background : 'rgba(51, 51, 51, 0.8)'
        }
      }
    };
    const setupOptions = Object.assign({}, defaults, videoOptions);

    this.uuid = uuidV4();

    this.resetDefaultPlaybackQuality();

    this.originalSetupOpitions = setupOptions;
    this.playerElementId = playerElementId;

    this.setupPlayer(playerElementId, setupOptions);
    this.resetDefaultVolume(setupOptions.mute);

    // This error handler handles the scenario where an old animoto mp4 video
    // render with bad enconding is loaded by JWPlayer. This will default the
    // provider to flash to put the player into a playable state
    this.player.on('error', (event) => {
      const errorCode = this.findErrorCodeByValue(event.message);
      if (errorCode === 'UNSUPPORTED_FILE_ENCODING' && this.getProviderName() === 'html5') {
        this.resetPlayer({ primary : 'flash' }, true);
      }
    });
  }

  /**
   * Instantiates a JWPlayer instance on an element calls setup on that instance.
   * Can be called multiple times on a player instance
   *
   * @method setupPlayer
   * @private
   *
   * @param  {String}  playerElementId   The ID of the element on the page in which to instantiate the player
   * @param  {Object}  setupOptions      All of the options necessary to instantiate the player (playlist, cover image, etc.)
   *
   * @example
   *   playerInstanceVariable.setupPlayer('player-element-id', {
   *    playlist: [{
   *      image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *      sources: [{
   *        file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *        label     : '1080p',
   *        'default' : true
   *      }]
   *    }],
   *    ctaSettings : {
   *      title : 'This is my CTA title',
   *      url   : 'http://www.animoto.com'
   *    },
   *    eventHandlers : {
   *      INITIALIZED : function(playerType) { console.log(playerType + ' was initialized'); },
   *      STARTED     : function(playerType) { console.log(playerType + ' started playing'); },
   *      COMPLETED   : function(playerType) { console.log(playerType + ' finished playing'); },
   *    },
   *    qosTracker : 'fake-domain.com/qos_trail.gif',
   *    qosStyle   : 'animoto_original',
   *    repeat     : false
   *    autostart  : false,
   *    mute       : false
   *  });
   */
  setupPlayer(playerElementId, setupOptions) {
    if (this.player) {
      this.player.remove();
    }

    this.player = window.jwplayer(playerElementId);

    if (setupOptions.playlist && setupOptions.allowPlaylistSorting) {
      this.sortPlaylistSources(setupOptions.playlist);
      this.setMinDvrWindowIfHLS(setupOptions.playlist);
    }

    this.player.setup(setupOptions);

    if (setupOptions.playlist) {
      this.seekReset(setupOptions.playlist);
    }

    if (setupOptions.eventHandlers) {
      this.bindHandlers(setupOptions.eventHandlers);
    }

    this.player.on('setupError', () => {
      document.getElementById(playerElementId).innerHTML = '<div class="jw-flash-required-message"><div class="jw-flash-header">Flash Player Required</div><div>Unfortunately we could not load the video player as it requires the Adobe Flash Player to be installed. Please <a href="http://get.adobe.com/flashplayer/" target="_blank" class="jw-flash-link">download the latest version</a>.</div></div>';
    });

    this.player.on('levelsChanged', (event) => {
      this.previousQualityLevelIndex = this.currentQualityLevelIndex;
      this.currentQualityLevelIndex = event.currentQuality;
    });

    this.player.on('ready', () => {
      const playlist = this.player.getPlaylist();
      const playlistSources = playlist[0] && playlist[0].sources;

      this.previousQualityLevelIndex = null;

      let currentQualityLevelIndex = this.currentQualityLevelIndex > -1 ? this.currentQualityLevelIndex : this.player.getCurrentQuality();
      let filteredSource;

      if (currentQualityLevelIndex < 0) {
        filteredSource = playlistSources.filter(source => source.default);
        currentQualityLevelIndex = playlistSources.indexOf(filteredSource[0]);
      }
      this.currentQualityLevelIndex = Math.max(currentQualityLevelIndex, 0);

      if (this.isHLS()) {
        const playerElement = document.getElementById(playerElementId);
        playerElement.classList.add('is-hls');

        const buttonContainerElement = playerElement.getElementsByClassName('jw-button-container')[0];
        if (buttonContainerElement) {
          const renderingMessageElement = document.createElement('h5');
          renderingMessageElement.id = 'renderingMessage';
          renderingMessageElement.className = 'hls-message';
          renderingMessageElement.innerHTML = 'Streaming';
          buttonContainerElement.appendChild(renderingMessageElement);
        }

        // The `.tooltip-time` element (scrub bar) is hidden for HLS mode because the progress bar
        // jumps around in a confusing way to users.  After the last chunk of the stream has been
        // loaded, however, the player switches to VOD mode.  The progress bar will then behave
        // predicably and can be shown, providing the user with the ability to scrub.
        //
        // I'm detecting this switch between HLS and VOD mode by looking for the duration to switch
        // between a negative to a positive, known value.
        const onTimeHandler = (obj) => {
          if (obj.duration > 0) { // This means the player switched from HLS to VOD
            this.player.off('time', onTimeHandler);
            if (!playerElement.classList.contains('vod-mode')) {
              playerElement.classList.add('vod-mode');
            }
            const messageElement = document.getElementById('renderingMessage');
            if (messageElement) { messageElement.remove(); }
          }
        };

        this.player.on('time', debounce(onTimeHandler, 300));
      }
    });

    // Because all seeks cause a play event in jwplayer, checking state of player
    // and then forcing it to pause if it was paused before the seek
    this.player.on('seek', () => {
      this.maintainPauseAfterSeek(this.player.getState());
    });

    // If there is a CTA set on the video
    if (setupOptions.ctaSettings) {
      this.setupCTA(setupOptions.ctaSettings);
    }

    if (setupOptions.qosTracker) {
      this.setupQOSBindings(setupOptions.qosTracker, setupOptions.qosStyle);
    }
  }

  /**
   * Resets player through calling of setupPlayer with opitional additional
   * setup options and allows for restarting of video playback
   *
   * @method resetPlayer
   *
   * @param {Object} additionalSetupOptions Additional setup options to merge into original setup options
   * @param {Boolean} shouldRestartPlayback Flag that determines whether to start playback once player is reset
   *
   */
  resetPlayer(additionalSetupOptions, shouldRestartPlayback) {
    this.setupPlayer(this.playerElementId, Object.assign({}, this.originalSetupOpitions, additionalSetupOptions));
    if (shouldRestartPlayback) {
      this.player.once('ready', () => { this.player.play(true); });
    }
  }
  /**
   * Sort playlist sources by label so that player HD toggle active and inactive styles
   * are set properly depending on playlist source length
   *
   * @method sortPlaylistSources
   * @private
   *
   * @param  {Array} playlist Playist array with playlist items
   *
   * @example
   *   this.sortPlaylistSources([{
         image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
         sources: [
          {
            file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4?t=720',
            label     : '720p'
          },
          {
            file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
            label     : '360p'
          },
          {
           file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4?t=480',
           label     : '480p',
           'default' : true
         }
       ]);
   */
  sortPlaylistSources(playlist) {
    playlist.forEach((item) => {
      const sources = item.sources;

      if (sources.length > 2) {
        sources.sort((a, b) => -a.label.localeCompare(b.label));
      } else {
        sources.sort((a, b) => a.label.localeCompare(b.label));
      }
    });
  }

  /**
   * Set minDvrWindow for playlist item if the source is hls
   *
   * @method setMinDvrWindowIfHLS
   *
   * @param {Object} playlist - playlist object for player
   *
   */
  setMinDvrWindowIfHLS(playlist) {
    playlist.forEach((item) => {
      if (item.sources[0].file.indexOf('.m3u8') !== -1) {
        item.minDvrWindow = 0;
      }
    });
  }

  /**
   * Resets the seek to 0 when playing for hls to actually start at the beginning
   *
   * @method seekReset
   *
   * @param {Object} playlist - playlist object for player
   *
   */
  seekReset(playlist) {
    const needsSeeking = playlist.some(item => item.sources[0].file.indexOf('.m3u8') !== -1);

    if (needsSeeking) {
      this.player.once('play', () => {
        this.player.seek(0);
      });
    }
  }

  /**
   * Check if current playback file is HLS stream
   *
   * @method isHLS
   *
   * @returns {Boolean} Is current playback HLS stream
   *
   */
  isHLS() {
    return this.getCurrentPlaybackFile().indexOf('.m3u8') !== -1;
  }

  /**
   * Get full path to current file being played
   *
   * @method getCurrentPlaybackFile
   *
   * @returns {String} Full path to playback file
   *
   */
  getCurrentPlaybackFile() {
    const sources = this.player.getPlaylist()[0].sources;
    const currentPlaybackSource = sources[this.currentQualityLevelIndex || 0];
    const currentPlaybackFile = currentPlaybackSource && currentPlaybackSource.file;

    return currentPlaybackFile || '';
  }

  /**
   * Set the volume on the wrapped player instance
   *
   * @method setVolume
   *
   * @param  {Integer} volume      The volume level on a 0 - 100 scale
   *
   * @example
   *   playerInstanceVariable.setVolume(20);
   */
  setVolume(volume) {
    this.player.setVolume(volume);
  }

  /**
   * Get the type of player being used (flash, html5, etc.)
   *
   * @method getProviderName
   *
   * @returns {String}  The type of provider currently being used for playback (html5, flash, etc.)
   *
   * @example
   *   var playerType = playerInstanceVariable.getProviderName();
   */
  getProviderName() {
    const provider = this.player.getProvider();
    return provider ? provider.name : '';
  }

  /**
   * Given an error message from JWPlayer returns an error code key
   *
   * @method findErrorCodeByValue
   * @private
   *
   * @param  {String} value Error message given by JWPlayer
   *
   * @returns {String} Key that maps to the value passed to function
   *
   * @example
   *   this.findErrorCodeByValue('Error loading player: Flash version must be 11.2 or greater');
   */
  findErrorCodeByValue(value) {
    return Object.keys(errorCodes).find(key => errorCodes[key] === value);
  }

  /**
   * Take the provided event handlers and bind them to the appropriate events triggered by JW Player
   * The event handler is always passed the value of getProviderName
   *
   * @method bindHandlers
   * @private
   *
   * @param  {Object} eventHandlers    An object where the keys are possible events, and the values are the callback
   *
   * @example
   *   this.bindHandlers({ INITIALIZED : function(playerType) { console.log('Player ready', playerType); } });
   */
  bindHandlers(eventHandlers) {
    // Loop through the properties of the object
    Object.keys(eventHandlers).forEach((k) => {
      let eventType;
      let eventHandler;

      switch (k) {
        case 'INITIALIZED':
          eventType = 'ready';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        case 'STARTED':
          eventType = 'play';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        case 'COMPLETED':
          eventType = 'complete';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        case 'SETUP_ERROR':
          eventType = 'setupError';
          eventHandler = this.bindErrorHandler(eventHandlers[k]);
          break;
        case 'ERROR':
          eventType = 'error';
          eventHandler = this.bindErrorHandler(eventHandlers[k]);
          break;
        case 'PLAYLIST_LOADED':
          eventType = 'playlist';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        case 'PROGRESS':
          eventType = 'time';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        case 'SEEKED':
          eventType = 'seeked';
          eventHandler = this.bindHandler(eventHandlers[k]);
          break;
        default:
          eventType = false;
      }

      if (eventType) {
        switch (eventType) {
          case 'ready':
          case 'play':
          case 'complete':
            this.player.once(eventType, eventHandler);
            break;
          default:
            this.player.on(eventType, eventHandler);
        }
      }
    });
  }

  /**
   * Recieves a function and binds this classes scope to it
   * The event handler is always passed the value of getProviderName
   *
   * @method bindHandler
   * @private
   *
   * @param {Function} handler A function that is an event handler
   *
   * @returns {Function} The passed in function wrapped in a function giving it this classes scope
   *
   * @example
   *   this.bindHandler(function(playerType) { console.log('Player ready', playerType); });
   */
  bindHandler(handler) {
    return (...args) => {
      // Call the event handler with the player 'type'
      handler(this.getProviderName(), ...args);
    };
  }

  /**
   * Recieves a function and binds this classes scope to it
   * The event handler is always passed the value of getProviderName
   * and the error code from errorCodes map
   *
   * @method bindErrorHandler
   * @private
   *
   * @param {Function} handler A function that is an event handler
   *
   * @returns {Function} The passed in function wrapped in a function giving it this classes scope
   *
   * @example
   *   this.bindErrorHandler(function(playerType, event, errorCode) { console.log('Player ready', playerType); });
   */
  bindErrorHandler(handler) {
    return (event) => {
      const errorCode = this.findErrorCodeByValue(event.message);
      // Call the event handler with the player 'type' and error code
      handler(this.getProviderName(), event, errorCode);
    };
  }

  /**
   * Loads a new playlist into an existing Player instance. Also removes current
   * default playback quality so that the "default" flag playlist source item
   * is honored
   *
   * @method loadPlaylist
   * @public
   *
   * @param {Array} playlist Loads a new playlist into an existing Player instance
   *
   * @example
   *   playerInstanceVariable.loadPlaylist([{
   *   image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *   sources: [
   *     {
   *       file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/720p.mp4',
   *       label     : '720p',
   *       'default' : true
   *     },
   *     {
   *      file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *      label     : '360p'
   *     }
   *    ]
   *  }]);
   */
  loadPlaylist(playlist) {
    this.resetDefaultPlaybackQuality();
    this.sortPlaylistSources(playlist);
    this.player.load(playlist);
    this.hideCTA();
  }

  /**
   * Returns the label of the current playback quality level
   *
   * @method getCurrentPlaybackQualityLabel
   * @public
   *
   * @returns {String} Current playback quality level
   *
   * @example
   *   this.getCurrentPlaybackQualityLabel();
   */
  getCurrentPlaybackQualityLabel() {
    const qualityLevels = this.player.getQualityLevels();
    const currentQualityLevelIndex = this.currentQualityLevelIndex;

    return (qualityLevels && (qualityLevels.length > 0) && (currentQualityLevelIndex >= 0)) ? qualityLevels[currentQualityLevelIndex].label : null;
  }

  /**
   * Clears jwplayer localStorage variable which determines playback quality
   *
   * @method resetDefaultPlaybackQuality
   * @private
   *
   * @example
   *   this.resetDefaultPlaybackQuality();
   */
  resetDefaultPlaybackQuality() {
    try {
      localStorage.removeItem('jwplayer.qualityLabel');
    } catch (e) {
      // localStorage is disabled
    }
  }

  /**
   * Resets volume and mute state to default values. JWPlayer
   * keeps state of both in local storage between player instances.
   *
   * @method resetDefaultVolume
   * @private
   *
   * @param {Boolean} muteValue Mute value to set on the player
   *
   * @example
   *   this.resetDefaultVolume();
   */
  resetDefaultVolume(muteValue = false) {
    this.player.setVolume(90);
    this.player.setMute(muteValue);
  }

  /**
   * Sets up a one time event listener for the "seeked" event to pause
   * the player if it was paused before seeking
   *
   * @method maintainPauseAfterSeek
   * @private
   *
   * @param {String} playState The play state on seeking
   *
   * @example
   *   this.maintainPauseAfterSeek('paused');
   */
  maintainPauseAfterSeek(playState) {
    if (playState === 'paused') {
      this.player.once('seeked', () => {
        this.player.pause();
      });
    }
  }

  /**
   * Seeks player to the point it was at before loading new playlist
   *
   * @method keepPlayPositionAfterPlaylistLoad
   * @public
   *
   * @param {String} preLoadPlayState The play state before loading playlist
   *
   * @example
   *   this.keepPlayPositionAfterPlaylistLoad('paused');
   */
  keepPlayPositionAfterPlaylistLoad(preLoadPlayState) {
    const position = this.player.getPosition();

    this.player.once('playlistItem', () => {
      this.maintainPauseAfterSeek(preLoadPlayState);
      if (position > 0) {
        this.player.seek(position);
      }
    });
  }

  /* ***********************************************************************************************
   *  CTA Related Functionality
   ** **********************************************************************************************/
  /**
   * Setup all of the CTA functionality
   *
   * @method setupCTA
   * @private
   *
   * @param  {Object} ctaSettings    An object containing the cta label and url
   *
   * @example
   *   this.setupCTA({ title : 'CTA Title', url : 'www.google.com' });
   */
  setupCTA(ctaSettings) {
    this.createCTA(ctaSettings);
    this.player.on('play', () => {
      this.hideCTA();
    });
    this.player.on('pause', () => {
      this.showCTA();
    });
    this.player.on('complete', () => {
      this.showCTA();
    });
  }

  /**
   * Take the CTA settings and create the appropriate HTML element, storing it on the class instance
   *
   * @method createCTA
   * @private
   *
   * @param  {Object} ctaSettings    An object containing the cta label and url
   *
   * @example
   *   this.createCTA({ title : 'CTA Title', url : 'www.google.com' });
   */
  createCTA(ctaSettings) {
    let url = ctaSettings.url;

    // Ensure that if the CTA didn't have a protocol, to default to basic http
    if ((url.indexOf('://') === -1) && (url.toLowerCase().indexOf('mailto:') === -1)) {
      url = `http://${url}`;
    }

    this.ctaElement = document.createElement('a');
    this.ctaElement.innerHTML = ctaSettings.title;
    this.ctaElement.setAttribute('href', url);
    this.ctaElement.setAttribute('target', '_blank');
    this.ctaElement.className = 'player_cta';
  }

  /**
   * Append the CTA element to the DOM to make it visible
   *
   * @method showCTA
   * @private
   *
   * @example
   *   this.showCTA();
   */
  showCTA() {
    if (this.ctaElement && !this.ctaElement.parentNode) {
      document.getElementById(this.playerElementId).appendChild(this.ctaElement);
    }
  }

  /**
   * Remove the CTA element from the DOM
   *
   * @method hideCTA
   * @private
   *
   * @example
   *   this.hideCTA();
   */
  hideCTA() {
    if (this.ctaElement && this.ctaElement.parentNode) {
      this.ctaElement.parentNode.removeChild(this.ctaElement);
    }
  }

  /* ***********************************************************************************************
   *  Quality of Service Tracking Related Functionality
   ** **********************************************************************************************/

  /**
   * @method setupQOSBindings
   * @private
   *
   * @param  {String} qosTracker    The url of the tracking pixel to insert into the page
   * @param  {String} qosStyle      (Optional) Style name of the video being played
   *
   * @example
   *   this.setupQOSBindings('https://animoto.com/trail.gif');
   */
  setupQOSBindings(qosTracker, qosStyle = false) {
    this.player.on('buffer', (details) => {
      this.loadCustomPixel(this.trackingUrl(qosTracker, 'buffering', {
        ps : details.oldstate,
        cs : details.newstate,
        r  : details.reason,
        sn : qosStyle
      }));
    });
    this.player.once('firstFrame', (details) => {
      this.loadCustomPixel(this.trackingUrl(qosTracker, 'firstFrame', {
        ttff : details.loadTime,
        sn   : qosStyle
      }));
    });
    this.player.on('levelsChanged', (event) => {
      const qualityLevels = this.player.getQualityLevels();
      const oldQuality = qualityLevels.length > 0 && qualityLevels[this.previousQualityLevelIndex];
      const newQuality = qualityLevels.length > 0 && qualityLevels[event.currentQuality];

      this.loadCustomPixel(this.trackingUrl(qosTracker, 'levelsChanged', {
        pq : oldQuality && oldQuality.label,
        cq : newQuality && newQuality.label
      }));
    });
  }

  /**
   * Inserts an image into the page to allow for tracking.  The CSS ensures the pixel isn't displayed
   *
   * @method loadCustomPixel
   * @private
   *
   * @param  {String} url    The url of the tracking pixel to insert into the page
   *
   * @example
   *   this.loadCustomPixel('https://animoto.com/trail.gif?tracking=thing');
   */
  loadCustomPixel(url) {
    const pixel = document.createElement('img');
    pixel.className = 'player_qos_tracking_pixel';
    pixel.setAttribute('src', url);
    pixel.setAttribute('alt', '');
    document.getElementById(this.playerElementId).appendChild(pixel);
  }

  /**
   * Returns okey of a file given a video or stream file url
   *
   * @method getOkeyFromFileURL
   * @private
   *
   * @param  {String} fileUrl File url of a video or stream
   *
   * @returns {String} The okey contained in file url
   *
   * @example
   *   var okey = this.getOkeyFromFileURL('https://s3.amazonaws.com/s3-s.animoto.com/Stream/MzUCXRIc6cAAq27QPu2Chg/hls.360p15.m3u8');
   */
  getOkeyFromFileURL(fileUrl) {
    const regEx = /\/(?:Video|Stream)\/(.+)\//;
    const matches = regEx.exec(fileUrl);

    return matches.length > 1 ? matches[1] : '';
  }

  /**
   * Forms the url of the pixel to load to track all of the data for the fired event
   *
   * @method trackingUrl
   * @private
   *
   * @param  {String} qosTracker      The url of the tracking pixel to insert into the page
   * @param  {String} eventType       The type of event that is being tracked
   * @param  {Object} additionalData  Any additional data specific to this event provided by the handler
   *
   * @returns {String}  The generated url including all of the data to be tracked
   *
   * @example
   *   var trackUrl = this.trackingUrl('https://animoto.com/trail.gif', 'start', {});
   */
  trackingUrl(qosTracker, eventType, additionalData) {
    const currentPlaybackFile = this.getCurrentPlaybackFile();

    let url = `//${qosTracker}`;

    url += '?s=player_qos_v1' + // eslint-disable-line prefer-template, scope
      '&e=' + eventType + // event
      '&src=' + document.location.hostname + // source
      '&pt=' + this.getProviderName() + // player type
      '&t=' + Math.round((new Date()).getTime() / 1000) + // time of event
      '&file=' + encodeURIComponent(currentPlaybackFile) + // current file
      '&okey=' + this.getOkeyFromFileURL(currentPlaybackFile) + // current okey
      '&res=' + this.getCurrentPlaybackQualityLabel() + // current resolution
      '&p=' + this.player.getPosition() + // playhead position
      '&d=' + this.player.getDuration() +
      '&uuid=' + this.uuid;

    if (additionalData) {
      Object.keys(additionalData).forEach((k) => {
        url += `&${encodeURIComponent(k)}=${encodeURIComponent(additionalData[k])}`;
      });
    }

    return url;
  }

  /**
   * Removes JPlayer instance and CTA DOM
   *
   * @method destroy
   * @public
   *
   * @example
   *   playerInstance.destroy();
   */
  destroy() {
    this.hideCTA();
    this.player.remove();
  }

}



// WEBPACK FOOTER //
// ./src/player.js